/**
 * Nowcasting cluster analysis: per-cluster forward alpha table +
 * strong-minus-weak long-short spread card.
 */
import React, { useState } from "react";
import { Badge } from "../ui/badge";
import {
  AlphaVal, BENCH_LABELS, HORIZON_LABELS, SigBadge, ToggleGroup,
} from "./common";

const QUALITY_VARIANT = {
  "Top Tier": "default",
  Strong: "secondary",
  Conditional: "outline",
  Weak: "destructive",
};

export const ClusterSection = ({ clusters, clusterMeta, hiConfSmw, horizons, testIdPrefix }) => {
  const [bench, setBench] = useState("sector");
  const [horizon, setHorizon] = useState(63);

  const key = `${bench}_${horizon}`;
  const data = clusters?.[key];
  const metaByCluster = Object.fromEntries((clusterMeta || []).map((m) => [m.cluster, m]));
  const smw = data?.strong_minus_weak;
  const hiConf = horizon === 63 ? hiConfSmw?.[bench] : null;

  const rows = (data?.per_cluster || [])
    .slice()
    .sort((a, b) => (b.mean_alpha_pct ?? -999) - (a.mean_alpha_pct ?? -999));

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-section`}>
      <div className="flex items-center gap-3 flex-wrap">
        <ToggleGroup
          testId={`${testIdPrefix}-bench`}
          options={[
            { value: "sector", label: BENCH_LABELS.sector },
            { value: "spy", label: BENCH_LABELS.spy },
          ]}
          value={bench}
          onChange={setBench}
        />
        <ToggleGroup
          testId={`${testIdPrefix}-horizon`}
          options={horizons.map((h) => ({ value: h, label: HORIZON_LABELS[h] || `${h}d` }))}
          value={horizon}
          onChange={setHorizon}
        />
      </div>

      {!data && (
        <div className="text-sm text-muted-foreground py-6 text-center">
          Insufficient data for this combination.
        </div>
      )}

      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Cluster table */}
          <div className="lg:col-span-2 overflow-x-auto">
            <table className="w-full text-sm" data-testid={`${testIdPrefix}-table`}>
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="text-left py-2 px-2 font-medium">Cluster</th>
                  <th className="text-left py-2 px-2 font-medium">Quality</th>
                  <th className="text-right py-2 px-2 font-medium">Alpha ({HORIZON_LABELS[horizon]})</th>
                  <th className="text-right py-2 px-2 font-medium">FM t</th>
                  <th className="text-right py-2 px-2 font-medium">Hit</th>
                  <th className="text-right py-2 px-2 font-medium">N</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const meta = metaByCluster[c.cluster] || {};
                  return (
                    <tr key={c.cluster} className="border-b hover:bg-muted/40">
                      <td className="py-2 px-2">
                        <span className="font-medium">{c.cluster}</span>
                        <span className="text-muted-foreground text-xs ml-2">{meta.name}</span>
                      </td>
                      <td className="py-2 px-2">
                        <Badge variant={QUALITY_VARIANT[meta.quality] || "outline"} className="text-xs">
                          {meta.quality}
                        </Badge>
                      </td>
                      <td className="text-right py-2 px-2"><AlphaVal value={c.mean_alpha_pct} /></td>
                      <td className="text-right py-2 px-2 font-mono text-xs">{c.fm_tstat ?? "-"}</td>
                      <td className="text-right py-2 px-2 font-mono text-xs">{c.hit_rate_pct ?? "-"}%</td>
                      <td className="text-right py-2 px-2 text-muted-foreground text-xs">
                        {c.n_obs?.toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Strong-minus-weak */}
          <div className="space-y-3">
            {smw && (
              <div className="border rounded-lg p-4 space-y-2" data-testid={`${testIdPrefix}-smw-card`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Strong − Weak Spread</span>
                  <SigBadge t={smw.nw_tstat} />
                </div>
                <div className="text-3xl font-bold">
                  <AlphaVal value={smw.mean_pct} />
                  <span className="text-sm font-normal text-muted-foreground ml-2">
                    per {HORIZON_LABELS[horizon]}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground space-y-1">
                  <div>Long clusters {`{1,4,5,6,8}`} / Short {`{3,7}`}</div>
                  <div>
                    NW t: <span className="font-mono text-foreground">{smw.nw_tstat ?? "-"}</span>
                    <span className="ml-3">Positive dates: <span className="font-mono text-foreground">{smw.pct_positive}%</span></span>
                  </div>
                  <div>
                    95% CI: <span className="font-mono text-foreground">[{smw.ci_lo_pct}, {smw.ci_hi_pct}]%</span>
                    <span className="ml-2">n={smw.n_dates}</span>
                  </div>
                </div>
              </div>
            )}
            {hiConf && (
              <div className="border rounded-lg p-4 space-y-1" data-testid={`${testIdPrefix}-hiconf-card`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">High-Confidence Subset</span>
                  <SigBadge t={hiConf.nw_tstat} />
                </div>
                <div className="text-xl font-bold"><AlphaVal value={hiConf.mean_pct} /></div>
                <div className="text-xs text-muted-foreground">
                  Strong − Weak using only above-median confidence calls (3M).
                  NW t: <span className="font-mono text-foreground">{hiConf.nw_tstat ?? "-"}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
